/**
 * HR Subscription Page
 * Screenshot-matching layout with duration selector and Pro Plus notify wiring
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import RazorpayCheckout from '../../components/Payment/RazorpayCheckout';
import { getPlanDetails } from '../../services/razorpayService';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { firebaseApp } from '../../firebase';

const db = getFirestore(firebaseApp);

const HRSubscription = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription, loading: subscriptionLoading, subscriptionStatus } = useSubscription();

  const [selectedDuration, setSelectedDuration] = useState('1 month');
  const [notifyEmail, setNotifyEmail] = useState('');

  const plan = getPlanDetails('HR', selectedDuration);
  const isProActive = subscription?.status === 'active' && subscription?.plan !== 'free' && subscription?.plan !== 'none';

  const durations = ['1 month', '3 months', '6 months', '12 months'];

  // Derived labels for header status chips (robust across old/new formats)
  const planLabel = (() => {
    const key = (subscription?.plan || subscriptionStatus?.planName || 'free')?.toLowerCase();
    if (!key || key === 'none') return 'Free';
    return key.charAt(0).toUpperCase() + key.slice(1);
  })();

  const statusLabel = (() => {
    const s = subscription?.status?.toLowerCase();
    const isActive = subscriptionStatus?.isActive || s === 'active';
    const isExpired = subscriptionStatus?.isExpired || s === 'expired';
    if (isActive) return 'Active';
    if (isExpired) return 'Expired';
    const base = s || 'inactive';
    return base.charAt(0).toUpperCase() + base.slice(1);
  })();

  const expiryDateVal = subscription?.expiresAt || subscriptionStatus?.endDate || null;
  const expiryLabel = expiryDateVal
    ? new Date(expiryDateVal).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  const isActiveStatus = (subscriptionStatus?.isActive === true) || (subscription?.status?.toLowerCase() === 'active');
  const isLifetime = isActiveStatus && !expiryDateVal;
  const expiryDisplay = isLifetime ? 'Lifetime' : expiryLabel;
  const statusTone = isActiveStatus ? 'bg-green-500' : (statusLabel === 'Expired' ? 'bg-red-500' : 'bg-amber-500');

  const handlePaymentSuccess = () => {
    toast.success('Subscription activated successfully!');
    setTimeout(() => navigate('/hr'), 800);
  };

  const handlePaymentFailure = (error) => {
    toast.error(error?.message || 'Payment failed. Please try again.');
  };

  const handleNotify = async () => {
    const email = (notifyEmail || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    try {
      await addDoc(collection(db, 'proPlusNotify'), {
        email,
        role: 'HR',
        userId: user?.id || null,
        createdAt: serverTimestamp(),
      });
      toast.success('We will notify you when Pro Plus launches');
      setNotifyEmail('');
    } catch (e) {
      console.error('Notify save error:', e);
      toast.error('Failed to save notification preference');
    }
  };

  if (subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-700 mx-auto" />
          <p className="mt-4 text-gray-600">Loading subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-orange-50 py-12 px-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-red-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-orange-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-20 left-1/2 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      <div className="max-w-[1920px] mx-auto relative z-10 px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-semibold mb-4">
            Subscription Plans
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-3">Choose Your Plan</h1>
          <p className="text-lg text-gray-600">Select the perfect plan to power your hiring</p>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-sm">
            <span className="inline-flex items-center gap-2 px-4 py-3 rounded-full bg-rose-100 text-slate-900 font-semibold shadow-sm border border-rose-200">
              <span className="h-2 w-2 rounded-full bg-rose-500" aria-hidden="true"></span>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-rose-700">Plan</span>
              <span className="text-slate-900 font-semibold">{planLabel}</span>
            </span>

            <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-green-100 text-slate-900 font-semibold shadow-sm border border-green-200">
              <span className={`h-2 w-2 rounded-full ${statusTone}`} aria-hidden="true"></span>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-green-800">Status</span>
              <span className="text-slate-900 font-semibold">{statusLabel}</span>
            </span>

            {expiryDisplay && (
              <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-100 text-slate-900 font-semibold shadow-sm border border-slate-200">
                <span className="h-2 w-2 rounded-full bg-slate-500" aria-hidden="true"></span>
                <span className="text-slate-900 font-semibold">Expires: {expiryDisplay}</span>
              </span>
            )}
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Basic */}
          <div className="bg-white rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.15)] border border-gray-200 p-8 transition-all duration-300 hover:shadow-[0_20px_60px_rgba(0,0,0,0.25)] hover:-translate-y-2">
            <div className="inline-block px-4 py-1.5 bg-gray-100 text-gray-700 text-sm font-semibold rounded-full mb-5">Free Forever</div>
            <h3 className="text-2xl font-bold text-gray-900">Basic</h3>
            <div className="mt-3 text-3xl font-extrabold">₹0<span className="text-lg font-medium text-gray-600">/month</span></div>
            <ul className="mt-8 space-y-4 text-base text-gray-700">
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">✓</span> Access to VetriaAI HR dashboard</li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">✓</span> Limited candidate invites per month</li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">✓</span> Standard support via tickets</li>
            </ul>
            {!isProActive && (
              <button className="mt-6 w-full bg-gray-100 text-gray-700 rounded-lg py-2.5 font-semibold" disabled>
                Current Plan
              </button>
            )}
          </div>

          {/* Core (Most Popular) */}
          <div className="relative bg-white rounded-xl shadow-[0_15px_50px_rgba(220,38,38,0.25)] border-2 border-red-400 p-8 transition-all duration-300 hover:shadow-[0_25px_70px_rgba(220,38,38,0.35)] hover:-translate-y-2">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-red-700 text-white text-sm font-bold px-5 py-1.5 rounded-full shadow-lg">Most Popular</span>
            </div>

            <>
              <h3 className="text-2xl font-bold text-gray-900">Core</h3>

              {/* Pricing Display */}
              <div className="mt-3">
                <div className="flex items-center gap-4">
                  <span className="text-xl font-medium text-gray-400 line-through">₹{plan.originalAmount}</span>
                  <span className="text-4xl font-extrabold text-red-700 bg-gradient-to-r from-red-700 via-red-500 to-red-700 bg-clip-text text-transparent animate-shimmer" style={{ backgroundSize: '200% auto' }}>₹{plan.amount}</span>
                </div>
                {selectedDuration !== '1 month' && (
                  <p className="text-base text-gray-600 font-medium mt-2">₹{Math.round(plan.amount / parseInt(selectedDuration))} per month</p>
                )}
                {selectedDuration === '1 month' && (
                  <p className="text-base text-gray-600 mt-2">Billed monthly</p>
                )}
              </div>

              {/* Duration Selector */}
              <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2">
                {durations.map((d) => (
                  <button
                    key={d}
                    onClick={() => setSelectedDuration(d)}
                    className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${selectedDuration === d ? 'border-red-600 bg-red-50 text-red-700 shadow-sm' : 'border-gray-200 hover:border-red-400 hover:shadow-sm'}`}
                  >
                    {d}
                  </button>
                ))}
              </div>

              <ul className="mt-8 space-y-4 text-base text-gray-700">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-red-700 font-bold">✓</span> {feature}
                  </li>
                ))}
              </ul>

              {isProActive ? (
                <div className="mt-6 space-y-2">
                  <button className="w-full bg-green-100 text-green-800 rounded-lg py-2.5 font-bold text-sm border-2 border-green-300" disabled>
                    ✓ Current Plan
                  </button>
                  {user && (
                    <RazorpayCheckout
                      amount={plan.amount}
                      planType="HR"
                      planDuration={plan.duration}
                      planName={plan.name}
                      userDetails={{
                        name: user.email || 'User',
                        email: user.email || '',
                        contact: '',
                      }}
                      onSuccess={handlePaymentSuccess}
                      onFailure={handlePaymentFailure}
                      buttonText="Extend Subscription"
                      buttonClassName="w-full bg-red-700 text-white hover:bg-red-800 transition-colors"
                    />
                  )}
                </div>
              ) : (
                user && (
                  <div className="mt-6">
                    <RazorpayCheckout
                      amount={plan.amount}
                      planType="HR"
                      planDuration={plan.duration}
                      planName={plan.name}
                      userDetails={{
                        name: user.email || 'User',
                        email: user.email || '',
                        contact: '',
                      }}
                      onSuccess={handlePaymentSuccess}
                      onFailure={handlePaymentFailure}
                      buttonText="Subscribe Now"
                      buttonClassName="w-full bg-red-700 text-white hover:bg-red-800 transition-colors"
                    />
                  </div>
                )
              )}
            </>
          </div>

          {/* Nexora (Premium) */}
          <div className="relative group">
            {/* Animated Glow Effect */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 via-orange-600 to-rose-600 rounded-2xl opacity-50 group-hover:opacity-100 blur transition duration-500 animate-tilt"></div>

            <div className="relative bg-white rounded-2xl p-8 h-full flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-600 to-rose-600 animate-gradient-x">
                    Nexora
                  </h3>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-bold uppercase tracking-wide border border-orange-100">
                    Coming Soon
                  </span>
                </div>

                <p className="text-gray-600 text-base leading-relaxed">
                  The Intelligent Recruitment Suite For Power Users
                </p>


              </div>

              <div className="mt-8">
                <p className="text-xs text-red-600 font-semibold mb-2 uppercase tracking-wide">Get Early Access</p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    className="flex-1 border border-red-100 bg-red-50/30 rounded-lg px-3 py-2.5 text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all"
                  />
                  <button
                    onClick={handleNotify}
                    className="px-4 py-2 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-lg text-sm font-bold shadow-lg shadow-red-200 hover:shadow-red-300 transform hover:-translate-y-0.5 transition-all"
                  >
                    Notify Me
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 mt-2 text-center">
                  Join the waitlist for <span className="font-bold text-red-600">Nexora</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16 bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-lg">Can I cancel anytime?</h3>
              <p className="text-gray-600 text-base">Yes, you can cancel your subscription at any time.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-lg">Is my payment secure?</h3>
              <p className="text-gray-600 text-base">Yes, we use Razorpay's secure payment gateway.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-lg">What support do I get?</h3>
              <p className="text-gray-600 text-base">Priority support included with paid plans.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-lg">Do you offer annual billing?</h3>
              <p className="text-gray-600 text-base">Yes, annual plans include savings compared to monthly billing.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRSubscription;
